import { useCallback, useState } from 'react'

import {
  type SourceKey,
  type SourceKeyInUse,
  anchorSourceKey,
  fetchSourceKeys,
  fetchSourceKeysInUse,
  releaseSourceKey,
} from '../../api/subjects'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { Button } from '../../ui/Control'
import { Failed, Waiting } from '../../ui/Status'

/**
 * What this subject is called in the systems content is taken over from.
 *
 * **It lives at the subject and nowhere else**, and that placement is the whole design of the
 * feature. A WebUntis lecture arrives carrying `ZPR`; somebody has to say once what `ZPR` means, and
 * that is a property of the subject in exactly the way its anchored folder is — "this is what it is
 * called elsewhere". Saying it at an appointment instead would put a second question about reach
 * ("this one, or all 37?") beside the one the appointment sheet already asks about a series, and
 * would make the same gesture mean two different things.
 *
 * **Tying a code reaches backwards**, and the screen says by how much. Every appointment carrying it
 * gets this subject — except the ones somebody set a subject on by hand, which a mapping does not
 * argue with — so something that changes a term's worth of entries has to be able to say so out
 * loud. That sentence is the reason the service answers with a count instead of nothing.
 *
 * The offer below is only the codes that belong to nobody yet. A code held by another subject is
 * shown there and not here: moving it is a decision about *that* subject, made where it is.
 */
export interface SourceKeysProps {
  readonly subjectId: string
  readonly subjectName: string
}

export function SourceKeys({ subjectId, subjectName }: SourceKeysProps) {
  const mine = useLoad(
    useCallback((signal: AbortSignal) => fetchSourceKeys(subjectId, signal), [subjectId]),
  )
  const inUse = useLoad(useCallback((signal: AbortSignal) => fetchSourceKeysInUse(signal), []))

  const [working, setWorking] = useState(false)
  const [said, setSaid] = useState<string | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)

  const held: readonly SourceKey[] = mine.load.status === 'ready' ? mine.load.value : []
  const everything: readonly SourceKeyInUse[] =
    inUse.load.status === 'ready' ? inUse.load.value : []
  const free = everything.filter((one) => one.subjectId === null)

  /*
    Nothing anywhere carries a code, so there is nothing to say. The section disappears rather than
    explaining a feature about systems that are not connected — an empty offer on every subject would
    be a permanent invitation to something nobody here can do.
  */
  const worthShowing = held.length > 0 || free.length > 0

  const attempt = async (work: () => Promise<number>, how: (followed: number) => string) => {
    setWorking(true)
    setFailure(null)
    try {
      const followed = await work()
      setSaid(how(followed))
      mine.retry()
      inUse.retry()
    } catch (error) {
      setFailure(asFailure(error))
    } finally {
      setWorking(false)
    }
  }

  const tie = (one: SourceKeyInUse) => {
    void attempt(
      async () => (await anchorSourceKey(subjectId, one.source, one.key)).followed,
      (followed) => reached(followed, one.key, subjectName, true),
    )
  }

  const untie = (one: SourceKey) => {
    void attempt(
      async () => (await releaseSourceKey(subjectId, one.source, one.key)).followed,
      (followed) => reached(followed, one.key, subjectName, false),
    )
  }

  if (mine.load.status === 'waiting' || inUse.load.status === 'waiting') {
    return (
      <section className="subject-detail__part">
        <h3 className="subject-detail__part-title">Called elsewhere</h3>
        <Waiting label="Looking up what this is called elsewhere." />
      </section>
    )
  }

  if (!worthShowing && failure === null) {
    return null
  }

  return (
    <section className="subject-detail__part">
      <h3 className="subject-detail__part-title">Called elsewhere</h3>
      <p className="subject-detail__aside">
        A timetable system spells a subject its own way. Say once which of its codes mean this
        subject, and everything that carries them — the whole term, backwards and forwards — belongs
        here. An appointment you gave a subject to by hand keeps yours.
      </p>

      {held.length > 0 && (
        <ul className="source-keys">
          {held.map((one) => (
            <li className="source-keys__one" key={`${one.source}:${one.key}`}>
              <span>
                <strong>{one.key}</strong> in {one.source}
              </span>
              <Button
                tone="bare"
                disabled={working}
                aria-label={`Untie ${one.key} from ${subjectName}`}
                onClick={() => {
                  untie(one)
                }}
              >
                Untie
              </Button>
            </li>
          ))}
        </ul>
      )}

      {free.length > 0 && (
        <>
          <p className="subject-detail__aside">
            {held.length === 0 ? 'Codes waiting for a subject:' : 'Still waiting for a subject:'}
          </p>
          <ul className="source-keys__free">
            {free.map((one) => (
              <li key={`${one.source}:${one.key}`}>
                <button
                  type="button"
                  className="chip"
                  disabled={working}
                  onClick={() => {
                    tie(one)
                  }}
                >
                  {one.name === '' ? one.key : `${one.name} (${one.key})`} · {howMany(one.count)}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {said !== null && (
        <p className="subject-detail__aside" role="status">
          {said}
        </p>
      )}
      {failure !== null && <Failed failure={failure} />}
    </section>
  )
}

/** How much content carries a code, in the words that fit on a chip. */
function howMany(count: number): string {
  return count === 1 ? '1 appointment' : `${String(count)} appointments`
}

/** What tying or untying a code just did, said with the number the service answered with. */
function reached(followed: number, key: string, subject: string, tied: boolean): string {
  if (followed === 0) {
    return tied
      ? `${key} means ${subject} from now on. Nothing already here needed changing.`
      : `${key} no longer means ${subject}. Nothing already here needed changing.`
  }
  const many = followed === 1 ? '1 appointment' : `${String(followed)} appointments`
  return tied
    ? `${key} means ${subject} from now on, and ${many} moved to it.`
    : `${key} no longer means ${subject}, and ${many} lost it again.`
}
